import { createSlice } from '@reduxjs/toolkit';
import {
  configIntegration,
  createInternalSettings,
  getInternalSettings,
  getTenantDetails,
  ingestCommunityGet,
  onboardCommunities,
  updateAccount,
  updateRestrictCommunication,
  validatePms,
  validateCrm,
  verifyCommunity,
  verifyRentCafe,
  getClients,
  updateClient,
  getCommunities,
  updateCommunity
} from '../Actions/SelfServe';

const initialState = {
  userData: [],
  successPopup: false,
  userDataError: false,
  communityId: false,
  individualCommunityData: {},
  apiLoading: false,
  clientsList: [],
  individualCommunity: [
    [
      {
        label: 'Community Name',
        name: 'communityName',
        value: '',
        error: false,
        disable: true,
        verify: false
      },
      {
        label: 'Time zone',
        value: { label: '', value: '' },
        disable: false,
        name: 'timeZone',
        type: 'select',
        error: false
      },
      {
        label: '',
        value: '',
        disable: false,
        name: 'tourDetailsAndLoadPNA',
        type: 'tourDetailsAndLoadPNA',
        error: false,
        verify: false
      }
    ],
    [
      {
        label: 'Knock ID',
        value: '',
        disable: false,
        name: 'knockId',
        endAdornment: 'verify',
        error: false,
        verify: false
      },
      {
        label: 'Kelsey ID',
        value: '',
        disable: true,
        name: 'kelseyId',
        error: false,
        verify: false
      },
      {
        label: 'House Account ID',
        name: 'houseAccountId',
        value: '',
        disable: true,
        error: false,
        verify: false
      }
    ],
    [
      {
        label: 'Company Code',
        value: '',
        disable: false,
        name: 'CompanyCode',
        error: false
      },
      {
        label: 'API Token',
        value: '',
        disable: false,
        name: 'rentcafeApiV2Token',
        error: false
      },
      {
        label: 'Property Code',
        value: '',
        disable: false,
        name: 'propertyCode',
        endAdornment: 'verify',
        error: false,
        verify: false
      }
    ]
  ],
  apartmentData: [],
  integrationApartment: [],
  selectedApartment: [],
  settingsList: {},
  isUserCreated: false,
  isUserUpdated: false,
  listingCommunity: true,
  validatePms: { verified: false, error: false },
  validateCrm: { verified: false, error: false },
  pendingUpload: false,
  configuredSettings: {
    tenantId: '',
    settingsName: '',
    pmsSetup: false,
    crmSetup: false,
    propertyIngestion: false,
    leadIngestion: false,
    assignLeadToKelsey: false,
    statusMappingsDefaultKnock: {
      source: 'knock',
      sourceType: 'CRM',
      mappings: [
        {
          stage: 'Inbound',
          overrideZumaStatus: false,
          thirdPartyStatuses: ['NEW']
        },
        {
          stage: 'Contacted',
          thirdPartyStatuses: ['OPEN', 'WAITING'],
          overrideZumaStatus: false
        },
        {
          stage: 'Tour Scheduled',
          thirdPartyStatuses: ['BOOKED'],
          overrideZumaStatus: true
        },
        {
          stage: 'Tour Cancelled',
          thirdPartyStatuses: ['CANCELLED_MANAGER', 'CANCELLED_RENTER'],
          overrideZumaStatus: true
        }
      ],
      allowedFirstContactTypes: [
        'internet',
        'email',
        'knock-schedule',
        'facebook',
        'sms',
        'landline'
      ]
    }
  },
  showSuccess: false
};

const SelfServeSlice = createSlice({
  name: 'selfServe',
  initialState,
  reducers: {
    openCommunity: (state, action) => {
      state.individualCommunity = initialState.individualCommunity;
    },
    verifyCommunityError: (state, action) => {
      const { name, value, step } = action.payload;
      const index = state.individualCommunity[step].findIndex(
        item => item.name === name
      );
      state.individualCommunity[step][index].value = value;
      state.individualCommunity[step][index].verify = false;
      state.individualCommunity[step][index].error = false;
    },
    addCommunityName: (state, action) => {
      state.individualCommunity[0][0].value = action.payload;
    },
    addTimeZone: (state, action) => {
      state.individualCommunity[0][1].value = action?.payload?.value;
      state.individualCommunity[0][1].disable = action.payload.disable
        ? true
        : false;
    },
    addPropertyCode: (state, action) => {
      state.individualCommunity[2][2].value = action?.payload?.value;
      state.individualCommunity[2][2].disable = action.payload.disable
        ? true
        : false;
    },
    addCompanyCode: (state, action) => {
      state.individualCommunity[2][0].value = action?.payload?.value;
      state.individualCommunity[2][0].disable = action.payload.disable
        ? true
        : false;
    },
    addRentcafeApiToken: (state, action) => {
      state.individualCommunity[2][1].value = action?.payload?.value;
      state.individualCommunity[2][1].disable = action.payload.disable
        ? true
        : false;
    },
    userCreationNotification: state => {
      state.userDataError = !state.userDataError;
    },
    listingCommunityNotification: state => {
      state.listingCommunity = !state.listingCommunity;
    },
    validatePmsError: (state, action) => {
      state.validatePms = { verified: false, error: action.payload || false };
    },
    validateCrmError: (state, action) => {
      state.validateCrm = { verified: false, error: action.payload || false };
    },
    addSelectedApartment: (state, action) => {
      state.selectedApartment = [...state.selectedApartment, action.payload];
    },
    removeSelectedApartment: (state, action) => {
      state.selectedApartment = action.payload;
    },
    addKnockRelatedDetails: (state, action) => {
      state.individualCommunity[1][0].value = action.payload?.knockId;
      state.individualCommunity[1][0].disable = true;
      state.individualCommunity[1][1].value = action.payload?.kelsey;
      state.individualCommunity[1][2].value = action.payload?.house;
    },
    configuredSettings: (state, action) => {
      state.configuredSettings = {
        ...state.configuredSettings,
        ...action.payload
      };
    },
    hideSuccessMessage: (state, action) => {
      state.showSuccess = false;
    },
    resetTenantData: state => {
      state.userData = initialState.userData;
      state.settingsList = initialState.settingsList;
      state.validatePms = initialState.validatePms;
      state.validateCrm = initialState.validateCrm;
      state.communityId = initialState.communityId;
      state.individualCommunityData = initialState.individualCommunityData;
      state.individualCommunity = initialState.individualCommunity;
      state.apartmentData = initialState.apartmentData;
      state.integrationApartment = initialState.integrationApartment;
      state.selectedApartment = initialState.selectedApartment;
      state.channelData = undefined;
      state.loadedTenantId = '';
      state.apiLoading = false;
      state.showSuccess = false;
    }
  },
  extraReducers: {
    updateClientList: (state, action) => {
      if (action?.payload) {
        state.clientsList = action.payload;
      }
    },
    [updateRestrictCommunication.pending]: (state, action) => {
      state.nonOwnerKelsey = false;
    },
    [updateRestrictCommunication.fulfilled]: (state, action) => {
      if (action?.payload) {
        state.nonOwnerKelsey = true;
      }
    },
    [updateRestrictCommunication.rejected]: (state, action) => {
      if (action?.payload) {
        state.nonOwnerKelsey = false;
      }
    },
    [updateAccount.pending]: (state, action) => {
      state.apiLoading = true;
    },
    [updateAccount.fulfilled]: (state, action) => {
      if (action?.payload) {
        state.userData = {
          ...state.userData,
          data: { ...state.userData?.data, ...action.payload.data }
        };
        state.apiLoading = false;
      }
    },
    [updateAccount.rejected]: (state, action) => {
      if (action?.payload) {
        state.userDataError = true;
        state.apiLoading = false;
      }
    },
    [verifyRentCafe.pending]: (state, action) => {
      state.apiLoading = true;
    },
    [verifyRentCafe.fulfilled]: (state, action) => {
      if (action?.payload) {
        state.verifyRentCafeData = action.payload.data;
        state.individualCommunity[2][2].verify = true;
        state.individualCommunity[2][2].error = false;
        state.apiLoading = false;
      }
    },
    [verifyRentCafe.rejected]: (state, action) => {
      if (action?.payload) {
        state.userDataError = true;
        state.verifyRentCafeData = false;
        state.individualCommunity[2][2].error = true;
        state.apiLoading = false;
      }
    },
    [getTenantDetails.fulfilled]: (state, action) => {
      if (action?.payload) {
        state.userData = action.payload?.data;
        state.loadedTenantId = action?.meta?.arg || '';
      }
    },
    [validatePms.pending]: (state, action) => {
      state.apiLoading = true;
    },
    [validatePms.fulfilled]: (state, action) => {
      if (action?.payload) {
        state.validatePms = {
          verified: action?.payload?.data?.data || false,
          error: !action?.payload?.data?.data
        };
        state.apiLoading = false;
      }
    },
    [validatePms.rejected]: (state, action) => {
      if (action?.payload) {
        state.validatePms = { verified: true, error: true };
        state.apiLoading = false;
      }
    },
    [validateCrm.pending]: state => {
      state.apiLoading = true;
    },
    [validateCrm.fulfilled]: (state, action) => {
      const success = action?.payload?.data?.data?.success;
      state.validateCrm = { verified: !!success, error: !success };
      state.apiLoading = false;
    },
    [validateCrm.rejected]: state => {
      state.validateCrm = { verified: false, error: true };
      state.apiLoading = false;
    },
    [verifyCommunity.pending]: (state, action) => {
      state.apiLoading = true;
    },
    [verifyCommunity.fulfilled]: (state, action) => {
      if (action.payload) {
        state.verifyCommunityKnockDetails = null;
        state.individualCommunity[1][0].verify = true;
        state.individualCommunity[1][2].value = action?.payload?.data?.data
          ?.meta?.knockHouseAgentUserName
          ? action?.payload?.data?.data?.meta?.knockHouseAgentUserName
          : '';
        state.individualCommunity[1][1].value = action?.payload?.data?.data
          ?.meta?.knockKelseyUserName
          ? action?.payload?.data?.data?.meta?.knockKelseyUserName
          : '';
        state.individualCommunity[1][0].error = false;

        state.apiLoading = false;
      }
    },
    [verifyCommunity.rejected]: (state, action) => {
      if (action.payload) {
        state.verifyCommunity = false;
        state.individualCommunity[1][0].error = true;
        state.individualCommunity[1][2].value = '';
        state.individualCommunity[1][1].value = '';
        state.apiLoading = false;
        state.verifyCommunityKnockDetails =
          action.payload?.response?.data?.errorData ?? null;
      }
    },
    [ingestCommunityGet.pending]: (state, action) => {
      state.apiLoading = true;
    },
    [ingestCommunityGet.fulfilled]: (state, action) => {
      if (action.payload) {
        const data = action?.payload?.data?.data;
        const filterCommunity = data?.filter(item => !item.integrations);
        state.apartmentData = filterCommunity || [];
        state.listingCommunity = true;
        state.selectedApartment = [];
        state.apiLoading = false;
      }
    },
    [ingestCommunityGet.rejected]: (state, action) => {
      if (action.payload) {
        state.apartmentData = [];
        state.listingCommunity = false;
        state.apiLoading = false;
      }
    },
    [createInternalSettings.pending]: (state, action) => {
      state.isSettingsUpdated = true;
      state.showSuccess = false;
    },
    [createInternalSettings.fulfilled]: (state, action) => {
      state.isSettingsUpdated = false;
      state.showSuccess = true;
    },
    [createInternalSettings.rejected]: (state, action) => {
      state.isSettingsUpdated = false;
      state.error = action.payload;
    },
    [getInternalSettings.pending]: (state, action) => {
      state.isSettingsUpdated = true;
    },
    [getInternalSettings.fulfilled]: (state, action) => {
      state.isSettingsUpdated = false;
      if (action?.payload?.data?.data) {
        state.settingsList = action.payload.data.data;
      }
    },
    [getInternalSettings.rejected]: (state, action) => {
      state.isSettingsUpdated = false;
      state.error = action.payload;
    },
    [onboardCommunities.pending]: state => {
      state.apiLoading = true;
    },
    [onboardCommunities.fulfilled]: state => {
      state.apiLoading = false;
    },
    [onboardCommunities.rejected]: state => {
      state.apiLoading = false;
    },
    [getCommunities.pending]: state => {
      state.apiLoading = true;
    },
    [getCommunities.fulfilled]: (state, action) => {
      if (action.payload) {
        state.integrationApartment = action.payload;
      }
      state.apiLoading = false;
    },
    [getCommunities.rejected]: state => {
      state.integrationApartment = [];
      state.apiLoading = false;
    },
    [configIntegration.pending]: (state, action) => {
      state.apiLoading = true;
    },
    [configIntegration.fulfilled]: (state, action) => {
      state.userData = action?.payload?.data;
      state.apiLoading = false;
    },
    [configIntegration.rejected]: (state, action) => {
      state.apiLoading = false;
    },
    [getClients.fulfilled]: (state, action) => {
      if (action?.payload) {
        state.clientsList = action.payload;
      }
    },
    [updateClient.fulfilled]: (state, action) => {
      if (action?.payload) {
        const updated = action.payload;
        state.clientsList = state.clientsList.map(client =>
          client.id === updated.id ? updated : client
        );
      }
    }
  }
});

export const {
  openCommunity,
  verifyCommunityError,
  userCreationNotification,
  listingCommunityNotification,
  addCommunityName,
  // addCommunityId,
  validatePmsError,
  validateCrmError,
  addTimeZone,
  addKnockRelatedDetails,
  removeSelectedApartment,
  addSelectedApartment,
  hideSuccessMessage,
  resetTenantData,
  addCompanyCode,
  addPropertyCode,
  addRentcafeApiToken
} = SelfServeSlice.actions;

export default SelfServeSlice;
